import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELDS, SECTIONS, TAT_COLORS } from "@/lib/fields";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Lock } from "lucide-react";

function FieldInput({ field, value, onChange, disabled }) {
  const tid = `field-${field.key}`;
  if (field.computed || field.auto) {
    if (field.key === "tat" && value) {
      return <span className="text-[11px] font-bold text-white px-2 py-1 rounded-sm inline-block" style={{ backgroundColor: TAT_COLORS[value] }} data-testid={tid}>{value}</span>;
    }
    return <div className="text-sm font-mono py-2 text-[#111827]" data-testid={tid}>{value ?? "—"}</div>;
  }
  if (field.type === "textarea") {
    return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={2} className="rounded-sm text-sm" data-testid={tid} />;
  }
  if (field.type === "select") {
    return (
      <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="rounded-sm" data-testid={tid}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>
          {(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  const type = { number: "number", date: "date", time: "time" }[field.type] || "text";
  return <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="rounded-sm text-sm" data-testid={tid} />;
}

export default function CaseDrawer({ caseId, open, onOpenChange, onSaved }) {
  const { canEdit } = useAuth();
  const isNew = !caseId;
  const hasEditable = FIELDS.some((f) => canEdit(f.key) && !f.computed);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      setData({});
      setForm({});
      return;
    }
    setLoading(true);
    api.get(`/cases/${caseId}`)
      .then((r) => { setData(r.data); setForm(r.data); })
      .catch((e) => { toast.error(apiError(e)); onOpenChange(false); })
      .finally(() => setLoading(false));
  }, [open, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  let saveLabel = "Save Changes";
  if (isNew) saveLabel = "Create Case";
  if (saving) saveLabel = "Saving...";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (!canEdit(k)) return;
        if (isNew || form[k] !== data[k]) {
          const f = FIELDS.find((x) => x.key === k);
          let v = form[k];
          if (v === "") v = null;
          if (v !== null && f?.type === "number") v = Number(v);
          if (v !== undefined) payload[k] = v;
        }
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        return;
      }
      if (isNew) {
        await api.post("/cases", payload);
        toast.success("Case created");
      } else {
        await api.patch(`/cases/${caseId}`, payload);
        toast.success("Case updated");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="case-drawer" aria-describedby={undefined}>
        <SheetHeader className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 z-10">
          <SheetTitle className="font-heading font-extrabold text-xl text-left">
            {isNew ? "New Case" : `Case ${form.ref_no || `#${form.sr_no ?? ""}`}`}
          </SheetTitle>
          {!isNew && form.tat && (
            <div className="flex items-center gap-3 pt-1">
              <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-sm" style={{ backgroundColor: TAT_COLORS[form.tat] }} data-testid="drawer-tat-badge">{form.tat}</span>
              <span className="text-xs text-[#4B5563] font-mono">{form.old_days ?? "—"} days old</span>
              {form.updated_by && <span className="text-[10px] text-[#9CA3AF]">Last update: {form.updated_by}</span>}
            </div>
          )}
        </SheetHeader>

        {loading ? (
          <div className="p-6 text-sm text-[#4B5563]">Loading case...</div>
        ) : (
          <div className="px-6 pb-24">
            <Accordion type="multiple" defaultValue={SECTIONS.map((s) => s.id)} className="w-full">
              {SECTIONS.map((section) => {
                const fields = FIELDS.filter((f) => f.section === section.id);
                const visible = fields.filter((f) => isNew ? canEdit(f.key) : (f.key in form || canEdit(f.key) || f.auto || f.computed));
                if (visible.length === 0) return null;
                return (
                  <AccordionItem key={section.id} value={section.id} className="border-b border-[#E5E7EB]">
                    <AccordionTrigger className="text-xs font-bold uppercase tracking-widest text-[#4B5563] hover:no-underline py-3" data-testid={`section-${section.id}`}>
                      {section.label}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 pb-4">
                        {visible.map((f) => {
                          const editable = canEdit(f.key) && !f.computed;
                          return (
                            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1">
                                {f.label}
                                {!editable && !f.computed && <Lock className="h-2.5 w-2.5" />}
                              </Label>
                              <div className="mt-1">
                                <FieldInput field={f} value={form[f.key]} onChange={(v) => set(f.key, v)} disabled={!editable} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}

        <div className="fixed bottom-0 right-0 w-full sm:max-w-2xl bg-white border-t border-[#E5E7EB] px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="drawer-cancel-button">Cancel</Button>
          {hasEditable && (
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="drawer-save-button">
              <Save className="h-4 w-4 mr-2" /> {saveLabel}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
